import React, { useMemo, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

import { Typography } from '@mui/material';

import QuotaListTable from '@ui-parts/QuotaListTable';
import QuotaListTableWithCollectCounts from '@ui-parts/QuotaListTableWithCollectCounts';
import DetailedConditionsDisplayArea from '@ui-parts/DetailedConditionsDisplayArea';

import {
  convertSpecialPanelCheckedItemsToArray,
  convertGenderToDisplayNameArray,
  convertAgeToDisplayNameArray,
  convertAreaCdsToDisplayNameArray,
  canEditSurveyStatus,
  calcEnqueteCollectSum,
} from '@functional';
import { LOCALE_STRING, SPECIAL_PANEL_CATEGORIES } from '~/constants';
import { FONT_WEIGHTS, FONT_SIZES } from '~/assets/styles/base';

const ContentBoxTitle = styled(Typography)`
  font-weight: ${FONT_WEIGHTS.size2};
`;

const EnqueteCollectTotalWrapper = styled.div`
  width: max-content;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const EnqueteCollectTotal = styled.div`
  margin: 0 1rem 0 2.4rem;
  font-size: ${FONT_SIZES.body5};
  font-weight: bold;
`;

const FlexBoxBetween = styled.div`
  display: flex;
  width: 100%;
  margin-top: 40px;
  justify-content: space-between;
`;

const ContentsWrapper = styled.div`
  margin: 20px 0;
`;

const QuotaListTableBox = styled.div`
  margin-top: 18px;
`;

const ConditionAreaWrapper = styled.div`
  margin-bottom: 20px;
  border: 1px solid #d6e0ea;
`;

function Quotas({ surveyStatusId }) {
  const { t } = useTranslation();
  const [enqueteCollectTotal, setEnqueteCollectTotal] = useState(0);

  /**
   * =============
   * selector
   * =============
   */
  const survey = useSelector((state) => state.survey);
  const areasSlice = useSelector((state) => state.areas);
  const quotasSlice = useSelector((state) => state.quotas);
  const qualificationsSlice = useSelector((state) => state.qualifications);
  const collectCounts = useSelector((state) => state.quotas.collectCounts);

  const shouldShowCollectCount = useMemo(() => {
    return !canEditSurveyStatus(surveyStatusId);
  }, [surveyStatusId]);

  const displayConditions = useMemo(() => {
    if (!survey.specialPanelDisplaySettingId) {
      return {};
    }
    // 固定条件である年齢・性別・地域の表示用文言を生成。
    const _displayConditions = {
      age: convertAgeToDisplayNameArray(qualificationsSlice.current.age).join('、'),
      gender: convertGenderToDisplayNameArray(qualificationsSlice.current.gender).join('、'),
      region: convertAreaCdsToDisplayNameArray(
        areasSlice.areas,
        qualificationsSlice.current.areas.checkedAreaCds
      ).join('、'),
    };
    // 表示設定によって動的に変わるスペパネ条件の表示用文言を生成。
    SPECIAL_PANEL_CATEGORIES[survey.specialPanelDisplaySettingId].forEach((category) => {
      _displayConditions[category] = convertSpecialPanelCheckedItemsToArray(
        qualificationsSlice.current.specialConditions.checkedItems[category],
        survey.specialPanelDisplaySettingId,
        true
      ).join('、');
    });
    return _displayConditions;
  }, [qualificationsSlice.current]);

  useEffect(() => {
    setEnqueteCollectTotal(calcEnqueteCollectSum(collectCounts));
  }, [collectCounts]);

  return (
    <>
      <ContentBoxTitle variant="title2">{t('quotas.deliveryTargetCondition')}</ContentBoxTitle>

      <ContentsWrapper>
        <ConditionAreaWrapper>
          <DetailedConditionsDisplayArea conditions={displayConditions} defExpand />
        </ConditionAreaWrapper>
      </ContentsWrapper>
      {canEditSurveyStatus(surveyStatusId) ? (
        <ContentBoxTitle variant="title2">
          {t('quotas.targetSamplesTotalBreakdown')}
        </ContentBoxTitle>
      ) : (
        <FlexBoxBetween>
          <ContentBoxTitle variant="title2">{t('quotas.currentEnqueteCounts')}</ContentBoxTitle>
          <EnqueteCollectTotalWrapper variant="title2">
            <div>{t('quotas.enqueteCollectTotalCounts')}</div>
            <EnqueteCollectTotal data-testid="EnqueteCollectTotalCounts">
              {enqueteCollectTotal.toLocaleString(LOCALE_STRING.ja)}
            </EnqueteCollectTotal>
          </EnqueteCollectTotalWrapper>
        </FlexBoxBetween>
      )}

      <QuotaListTableBox>
        <ContentsWrapper>
          {shouldShowCollectCount ? (
            <QuotaListTableWithCollectCounts
              ages={quotasSlice.ages}
              enqueteCollectCounts={collectCounts}
              targetSamples={quotasSlice.targetSamples}
              qualificationsGender={qualificationsSlice.current.gender}
              isAdmin
            />
          ) : (
            <QuotaListTable
              ages={quotasSlice.ages}
              quotas={quotasSlice.quotas}
              targetSamples={quotasSlice.targetSamples}
              qualificationsGender={qualificationsSlice.current.gender}
              isAdmin
            />
          )}
        </ContentsWrapper>
      </QuotaListTableBox>
    </>
  );
}

Quotas.propTypes = {
  surveyStatusId: PropTypes.number,
};

export default Quotas;
